import { useFirestoreDoc } from "@p0security/frontend-core/react";
import {
  DEFAULT_MAX_ACCESS_DURATION,
  DEFAULT_STANDING_ACCESS_DURATION,
  durationSettingsPath,
} from "@p0security/shared/configuration/constant";
import {
  type DurationOption,
  type LegacyTenantDurationSettings,
  TenantDurationSettings,
} from "@p0security/shared/configuration/types";
import { sortApprovalOptions } from "@p0security/shared/permission-requests/util";
import { useContext, useMemo } from "react";

import { Tenant } from "../Login";

// TODO Post migration in CUS-909 remove legacy fallback and read TenantDurationSettings directly
// Mirrors the backwards-compatible remap in backend loadDurationSettings
const normalizeDurationSettings = (
  data: TenantDurationSettings & LegacyTenantDurationSettings
): TenantDurationSettings => {
  const normalized: TenantDurationSettings & LegacyTenantDurationSettings = {
    ...data,
  };

  if (
    !normalized.maxStandingAccessDuration &&
    normalized.standingAccessDuration
  ) {
    normalized.maxStandingAccessDuration = normalized.standingAccessDuration;
    delete normalized.standingAccessDuration;
  }

  return normalized;
};

export const useDurationSettings = () => {
  const tenantId = useContext(Tenant);
  const result = useFirestoreDoc<TenantDurationSettings>(
    durationSettingsPath(tenantId),
    { live: true }
  );

  return useMemo(() => {
    if (!result.doc) return result;
    // Only data is normalized; keep the KnownFirestoreDoc shape so callers read optionsDoc.doc?.data unchanged for this temp migration-compatibility code
    return {
      ...result,
      doc: { ...result.doc, data: normalizeDurationSettings(result.doc.data) },
    };
  }, [result]);
};

/** Tenant max and standing max as the backend applies them: defaults when
 * unsaved, standing capped at max */
export const resolveTenantMaxDurations = (
  data: TenantDurationSettings | undefined
): { max: DurationOption; standing: DurationOption } => {
  const max = data?.maxAccessDuration || DEFAULT_MAX_ACCESS_DURATION;
  const standing =
    data?.maxStandingAccessDuration || DEFAULT_STANDING_ACCESS_DURATION;
  return {
    max,
    standing: sortApprovalOptions(standing, max) > 0 ? max : standing,
  };
};

// TODO Post CUS-909 migration use this commented code only
// export const useDurationSettings = () => {
//   const tenantId = useContext(Tenant);
//   return useFirestoreDoc<TenantDurationSettings>(
//     durationSettingsPath(tenantId),
//     {
//       live: true,
//     }
//   );
// };
